import { useFormContext } from 'react-hook-form';
import { Grid, Button, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useEffect } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { selectAllPeopleNames } from '../../../../../api/peopleApiSlice';
import FormTextField from '../../../../../components/FormFields/FormTextField';
import FormListSelect from '../FormListSelect';
import FormTimePicker from '../FormTimePicker';
import MukanaOlevat from '../MukanaOlevat';
import RichTextEditor from '../../../../../components/RichTextEditor';

dayjs.extend(utc);
dayjs.extend(timezone);

const ScheduleInputs = ({ fieldName, kansanopisto, setHours, onClose }) => {
    const { register, setValue, getValues, trigger, formState: { errors } } = useFormContext();
    const people = useSelector(selectAllPeopleNames);

    if (!fieldName || typeof fieldName !== 'string') {
        console.error('ScheduleInputs: Invalid fieldName', { fieldName });
        return <Typography color="error">Error: Invalid field name</Typography>;
    }

    const handleSave = async () => {
        const isValid = await trigger(fieldName);
        const fieldValue = getValues(fieldName);

        if (isValid) {
            const tilaisuus = getValues(fieldName);
            const kansanopistoHours = tilaisuus?.kansanopistotunnit?.[0]?.kansanopistotunnit || 0;
            const previousHours = getValues(`${fieldName}.kansanopistotunnit.0.kansanopistotunnit`) || 0;

            setHours((prevTotal) => prevTotal - previousHours + kansanopistoHours);
            setValue(fieldName, tilaisuus, { shouldDirty: true, shouldTouch: true });
            await trigger(fieldName);
            onClose();
        }
    };

    useEffect(() => {
        const tilaisuus = getValues(fieldName);
        if (!tilaisuus?.id) {
            setValue(`${fieldName}.id`, crypto.randomUUID(), { shouldDirty: true });
        }
    }, [fieldName, getValues, setValue]);

    // Helper to get nested error
    const getNestedError = (field) => {
        const parts = field.split('.');
        let error = errors;
        for (const part of parts) {
            error = error?.[part];
            if (!error) return null;
        }
        return error?.message || null;
    };

    return (
        <Grid
            container
            spacing={{ xs: 1, sm: 2 }}
            sx={{ p: { xs: 1, sm: 2 }, flexGrow: 1 }}
        >
            <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
                    Tilaisuuden tiedot
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <FormTextField
                    fieldName={`${fieldName}.nimi`}
                    label="Tilaisuuden nimi"
                    variant="outlined"
                    placeholder="Tilaisuuden nimi"
                    fullWidth
                    error={!!getNestedError(`${fieldName}.nimi`)}
                    helperText={getNestedError(`${fieldName}.nimi`)}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormTimePicker
                    fieldName={`${fieldName}.aloitus`}
                    label="Tilaisuuden alkamisaika"
                    setValue={setValue}
                    getValues={getValues}
                    error={getNestedError(`${fieldName}.aloitus`)}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormTimePicker
                    fieldName={`${fieldName}.lopetus`}
                    label="Tilaisuuden päättymisaika"
                    setValue={setValue}
                    getValues={getValues}
                    error={getNestedError(`${fieldName}.lopetus`)}
                />
            </Grid>
            <Grid
                item
                xs={12}
                sx={{
                    maxHeight: { xs: '150px', sm: '200px' },
                    overflowY: 'auto',
                }}
            >
                <RichTextEditor
                    fieldName={`${fieldName}.kuvaus`}
                    error={!!getNestedError(`${fieldName}.kuvaus`)}
                    helperText={getNestedError(`${fieldName}.kuvaus`)}
                />
            </Grid>
            <Grid item xs={12}>
                <MukanaOlevat
                    fieldName={`${fieldName}.henkilot`}
                    subfields={[
                        { name: 'henkilo', options: people, xs: 9 },
                        { name: 'titteli', options: ['Kyllä', 'Ei'], xs: 3, label: 'Näytä titteli' },
                    ]}
                    emptyMsg="Tilaisuudella ei vielä ole henkilöitä"
                    error={!!getNestedError(`${fieldName}.henkilot`)}
                    helperText={getNestedError(`${fieldName}.henkilot`)}
                />
            </Grid>
            {kansanopisto && (
                <Grid item xs={12}>
                    <FormListSelect
                        setHours={setHours}
                        fieldName={`${fieldName}.kansanopistotunnit`}
                        subfields={[
                            {
                                name: 'kansanopistotunnit',
                                options: [...Array(11).keys()],
                                xs: 12,
                                label: 'Kansanopistotunnit',
                            },
                        ]}
                        emptyMsg="Tilaisuudella ei ole kansanopistotunteja"
                        error={!!getNestedError(`${fieldName}.kansanopistotunnit`)}
                        helperText={getNestedError(`${fieldName}.kansanopistotunnit`)}
                    />
                </Grid>
            )}
            <Grid
                item
                xs={12}
                display="flex"
                justifyContent="center"
                sx={{ mt: { xs: 1, sm: 2 } }}
            >
                <Button
                    variant="contained"
                    onClick={handleSave}
                    sx={{
                        backgroundColor: 'taphaBlue.main',
                        color: 'white',
                        px: 3,
                        py: 1,
                        borderRadius: 1,
                        textTransform: 'none',
                        '&:hover': {
                            backgroundColor: 'taphaBlue.dark',
                        },
                    }}
                >
                    Tallenna ja palaa
                </Button>
            </Grid>
        </Grid>
    );
};

ScheduleInputs.propTypes = {
    fieldName: PropTypes.string.isRequired,
    kansanopisto: PropTypes.any,
    setHours: PropTypes.func,
    onClose: PropTypes.func,
};

export default ScheduleInputs;